import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import RouteChangeTracker from "@/components/RouteChangeTracker";
import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import TeamPage from "@/pages/TeamPage";
import ContactPage from "@/pages/ContactPage";
import FaqsPage from "@/pages/FaqsPage";

// Admin bundle — code-split so it never ships to public-site visitors.
import { AuthProvider } from "@/admin/AuthContext";
const AdminLogin       = lazy(() => import("@/admin/AdminLogin"));
const AdminLayout      = lazy(() => import("@/admin/AdminLayout"));
const AdminDashboard   = lazy(() => import("@/admin/AdminDashboard"));
const ServicesAdmin    = lazy(() => import("@/admin/ServicesAdmin"));
const BlogAdmin        = lazy(() => import("@/admin/BlogAdmin"));
const TeamAdmin        = lazy(() => import("@/admin/TeamAdmin"));
const FaqsAdmin        = lazy(() => import("@/admin/FaqsAdmin"));
const ContactsAdmin    = lazy(() => import("@/admin/ContactsAdmin"));
const SettingsAdmin    = lazy(() => import("@/admin/SettingsAdmin"));
const CaseStudiesAdmin = lazy(() => import("@/admin/CaseStudiesAdmin"));

const AdminLoading = () => (
  <div className="min-h-screen bg-ink flex items-center justify-center text-gray-500 text-sm">
    Loading admin…
  </div>
);

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <RouteChangeTracker />
          <AuthProvider>
            <Routes>
              {/* Admin */}
              <Route path="/admin/login" element={
                <Suspense fallback={<AdminLoading />}><AdminLogin /></Suspense>
              } />
              <Route path="/admin" element={
                <Suspense fallback={<AdminLoading />}><AdminLayout /></Suspense>
              }>
                <Route index element={<Suspense fallback={<AdminLoading />}><AdminDashboard /></Suspense>} />
                <Route path="services" element={<Suspense fallback={<AdminLoading />}><ServicesAdmin /></Suspense>} />
                <Route path="case-studies" element={<Suspense fallback={<AdminLoading />}><CaseStudiesAdmin /></Suspense>} />
                <Route path="blog" element={<Suspense fallback={<AdminLoading />}><BlogAdmin /></Suspense>} />
                <Route path="team" element={<Suspense fallback={<AdminLoading />}><TeamAdmin /></Suspense>} />
                <Route path="faqs" element={<Suspense fallback={<AdminLoading />}><FaqsAdmin /></Suspense>} />
                <Route path="contacts" element={<Suspense fallback={<AdminLoading />}><ContactsAdmin /></Suspense>} />
                <Route path="settings" element={<Suspense fallback={<AdminLoading />}><SettingsAdmin /></Suspense>} />
              </Route>

              {/* Public site */}
              <Route element={<Layout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/services/:slug" element={<ServiceDetailPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/testimonials" element={<TestimonialsPage />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/faqs" element={<FaqsPage />} />

                {/* ─── Legacy URL redirects (from the previous WordPress site) ───
                   These pages are still indexed by Google. `<Navigate replace>`
                   sends visitors and (over time) crawlers to the current equivalent.
                   Note: React Router redirects are client-side 200s not 301s, but
                   Google follows and updates its index within a few crawls. */}
                <Route path="/frequently-asked-questions" element={<Navigate to="/faqs" replace />} />
                <Route path="/frequently-asked-questions/*" element={<Navigate to="/faqs" replace />} />
                <Route path="/feasibility-scoping-reports" element={<Navigate to="/services/feasibility" replace />} />
                <Route path="/feasibility-scoping-reports/*" element={<Navigate to="/services/feasibility" replace />} />
                <Route path="/portfolio-items/*" element={<Navigate to="/services" replace />} />
                <Route path="/services-2" element={<Navigate to="/services" replace />} />
                <Route path="/services-2/*" element={<Navigate to="/services" replace />} />
                <Route path="/about" element={<Navigate to="/team" replace />} />
                <Route path="/about-us" element={<Navigate to="/team" replace />} />
                <Route path="/home" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="bottom-right" />
      </HelmetProvider>
    </div>
  );
}

export default App;
